/**
 * Entity opacity for hotspots, portals and custom meshes, composited correctly against the splats.
 *
 * The problem: gaussian splats are alpha-blended back to front and write no depth. Opaque meshes
 * work because they draw first (HotspotMeshLayer sits right after the World opaque sub-layer),
 * write depth, and the splats depth-test against them. A translucent mesh cannot draw first
 * (it would blend against the clear colour and hide the splats behind it), and drawing it after
 * the splats with a plain depth test would put it on top of splats that should hide it.
 *
 * The fix, in three parts:
 * 1. Materials of an entity below full opacity are switched to alpha blending with depth write
 *    off (this module), and the entity moves to TranslucentEntityLayer, which renders right after
 *    the splats (createViewer.ts).
 * 2. While any such entity is on screen, SplatDepthPass renders the splats' depth into a
 *    linear-depth texture (splats above ~30% alpha count as a surface), before the main pass.
 * 3. The translucent material's opacity chunk compares each fragment's depth with that texture
 *    and fades the fragment out where a splat surface is in front of it, over a short soft
 *    range so the per-splat depth steps do not show as a hard edge.
 *
 * Texture alpha (PNG icons, GLB cutouts, video/GIF alpha) is preserved: the entity opacity
 * multiplies the material's own alpha, and alphaTest keeps testing the material's own alpha, so
 * cutouts stay cut out at any entity opacity.
 *
 * A material whose OWN alpha must blend over the splats (a transparent video: soft edges, glows,
 * semi-transparent areas) is marked with setTextureTranslucent and stays on this translucent path
 * at every entity opacity, including 1. Cut-out media (PNG icons, alphaTest 0.5) does not need it.
 */
import * as pc from 'playcanvas';

/** Opacities at or above this are treated as fully opaque (originals restored, layer back to opaque). */
export const FULL_OPACITY_THRESHOLD = 0.999;
const OPACITY_EPSILON = 0.001;

/** Uniform names shared with SplatDepthPass. */
export const ENTITY_OPACITY_UNIFORM = 'uEntityOpacity';
export const SPLAT_DEPTH_MAP_UNIFORM = 'uSplatDepthMap';
/** x: 1 when the depth map is valid this frame, y: soft range as a fraction of depth, z: minimum soft range (world units). */
export const SPLAT_DEPTH_PARAMS_UNIFORM = 'uSplatDepthParams';

export const SPLAT_DEPTH_OPACITY_GLSL = /* glsl */ `
uniform float material_opacity;
uniform float material_alphaDitherScale;
uniform float uEntityOpacity;
uniform vec4 uSplatDepthParams;
uniform highp sampler2D uSplatDepthMap;
#ifndef VIEWPORT_SIZE
    #define VIEWPORT_SIZE
    uniform vec4 viewport_size;
#endif
#ifndef CAMERAPLANES
    #define CAMERAPLANES
    uniform vec4 camera_params;
#endif
#ifndef ENTITY_ALPHA_RAW
    #define ENTITY_ALPHA_RAW
    float dEntityAlphaRaw;
#endif
float splatDepthAt(vec2 uv) {
    ivec2 size = textureSize(uSplatDepthMap, 0);
    ivec2 texel = clamp(ivec2(uv * vec2(size)), ivec2(0), size - 1);
    vec4 data = texelFetch(uSplatDepthMap, texel, 0);
    #ifdef CAPS_TEXTURE_FLOAT_RENDERABLE
        return data.r;
    #else
        uint bits = (uint(data.r * 255.0) << 24u) | (uint(data.g * 255.0) << 16u) | (uint(data.b * 255.0) << 8u) | uint(data.a * 255.0);
        return uintBitsToFloat(bits);
    #endif
}
float entityFragLinearDepth() {
    if (camera_params.w == 0.0) return 1.0 / gl_FragCoord.w;
    return camera_params.z + gl_FragCoord.z * (camera_params.y - camera_params.z);
}
void getOpacity() {
    dAlpha = material_opacity;
    #ifdef STD_OPACITY_TEXTURE
    dAlpha *= texture2DBias({STD_OPACITY_TEXTURE_NAME}, {STD_OPACITY_TEXTURE_UV}, textureBias).{STD_OPACITY_TEXTURE_CHANNEL};
    #endif
    #ifdef STD_OPACITY_VERTEX
    dAlpha *= clamp(vVertexColor.{STD_OPACITY_VERTEX_CHANNEL}, 0.0, 1.0);
    #endif
    dEntityAlphaRaw = dAlpha;
    float splatVisibility = 1.0;
    if (uSplatDepthParams.x > 0.5) {
        // The engine updates viewport_size for each render target, including Render Scale.
        float splatDepth = splatDepthAt(gl_FragCoord.xy * viewport_size.zw);
        float fragDepth = entityFragLinearDepth();
        float soft = max(uSplatDepthParams.z, fragDepth * uSplatDepthParams.y);
        splatVisibility = clamp((splatDepth - fragDepth) / soft + 0.5, 0.0, 1.0);
    }
    dAlpha *= uEntityOpacity * splatVisibility;
}
`;

export const SPLAT_DEPTH_ALPHA_TEST_GLSL = /* glsl */ `
uniform float alpha_ref;
#ifndef ENTITY_ALPHA_RAW
    #define ENTITY_ALPHA_RAW
    float dEntityAlphaRaw;
#endif
void alphaTest(float a) {
    if (dEntityAlphaRaw < alpha_ref) discard;
}
`;

export const SPLAT_DEPTH_OPACITY_WGSL = /* wgsl */ `
uniform material_opacity: f32;
uniform material_alphaDitherScale: f32;
uniform uEntityOpacity: f32;
uniform uSplatDepthParams: vec4f;
var uSplatDepthMap: texture_2d<uff>;
#ifndef VIEWPORT_SIZE
    #define VIEWPORT_SIZE
    uniform viewport_size: vec4f;
#endif
#ifndef CAMERAPLANES
    #define CAMERAPLANES
    uniform camera_params: vec4f;
#endif
#ifndef ENTITY_ALPHA_RAW
    #define ENTITY_ALPHA_RAW
    var<private> dEntityAlphaRaw: f32;
#endif
fn splatDepthAt(uv: vec2f) -> f32 {
    let size = vec2i(textureDimensions(uSplatDepthMap, 0));
    let texel = clamp(vec2i(uv * vec2f(size)), vec2i(0), size - vec2i(1));
    return textureLoad(uSplatDepthMap, texel, 0).r;
}
fn entityFragLinearDepth() -> f32 {
    if (uniform.camera_params.w == 0.0) {
        return 1.0 / pcPosition.w;
    }
    return uniform.camera_params.z + pcPosition.z * (uniform.camera_params.y - uniform.camera_params.z);
}
fn getOpacity() {
    dAlpha = uniform.material_opacity;
    #ifdef STD_OPACITY_TEXTURE
    dAlpha = dAlpha * textureSampleBias({STD_OPACITY_TEXTURE_NAME}, {STD_OPACITY_TEXTURE_NAME}Sampler, {STD_OPACITY_TEXTURE_UV}, uniform.textureBias).{STD_OPACITY_TEXTURE_CHANNEL};
    #endif
    #ifdef STD_OPACITY_VERTEX
    dAlpha = dAlpha * clamp(vVertexColor.{STD_OPACITY_VERTEX_CHANNEL}, 0.0, 1.0);
    #endif
    dEntityAlphaRaw = dAlpha;
    var splatVisibility: f32 = 1.0;
    if (uniform.uSplatDepthParams.x > 0.5) {
        // The engine updates viewport_size for each render target, including Render Scale.
        let splatDepth = splatDepthAt(pcPosition.xy * uniform.viewport_size.zw);
        let fragDepth = entityFragLinearDepth();
        let soft = max(uniform.uSplatDepthParams.z, fragDepth * uniform.uSplatDepthParams.y);
        splatVisibility = clamp((splatDepth - fragDepth) / soft + 0.5, 0.0, 1.0);
    }
    dAlpha = dAlpha * uniform.uEntityOpacity * splatVisibility;
}
`;

export const SPLAT_DEPTH_ALPHA_TEST_WGSL = /* wgsl */ `
uniform alpha_ref: f32;
#ifndef ENTITY_ALPHA_RAW
    #define ENTITY_ALPHA_RAW
    var<private> dEntityAlphaRaw: f32;
#endif
fn alphaTest(a: f32) {
    if (dEntityAlphaRaw < uniform.alpha_ref) {
        discard;
    }
}
`;

/** The subset of StandardMaterial the helper touches, so unit tests can pass a plain object. */
export interface TranslucentCapableMaterial {
    blendType: number;
    depthWrite: boolean;
    depthTest: boolean;
    getShaderChunks(language: string): Map<string, string>;
    setParameter(name: string, data: number | number[] | Float32Array): void;
    update(): void;
}

interface OriginalMaterialState {
    blendType: number;
    depthWrite: boolean;
    depthTest: boolean;
    chunks: Record<string, Record<string, string | undefined>>;
}

/** Remembered per material the first time it goes translucent, so full opacity restores the authored look. */
const originalState = new WeakMap<object, OriginalMaterialState>();
/** Materials currently rendered translucent by entity opacity, with the opacity last applied. */
const translucentMaterials = new WeakMap<object, number>();
/**
 * Temporary clones that stand in for a material on its mesh instances (the editor's selection
 * highlight). Every entity-opacity write to the source is mirrored onto them, so the clone on
 * screen follows opacity edits and the source stays current for when it is put back.
 */
const linkedClones = new WeakMap<object, Set<TranslucentCapableMaterial>>();
/**
 * Materials whose own texture alpha blends over the splats (setTextureTranslucent). They are
 * rendered translucent at any entity opacity, so even at full opacity they draw after the splats.
 */
const textureTranslucentMaterials = new WeakSet<object>();

const CHUNKS: Record<string, Record<string, string>> = {
    [pc.SHADERLANGUAGE_GLSL]: { opacityPS: SPLAT_DEPTH_OPACITY_GLSL, alphaTestPS: SPLAT_DEPTH_ALPHA_TEST_GLSL },
    [pc.SHADERLANGUAGE_WGSL]: { opacityPS: SPLAT_DEPTH_OPACITY_WGSL, alphaTestPS: SPLAT_DEPTH_ALPHA_TEST_WGSL },
};

function clamp01(value: number): number {
    return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** True when the material is currently rendered translucent by entity opacity. */
export function isEntityTranslucentMaterial(material: object | null | undefined): boolean {
    return !!material && translucentMaterials.has(material);
}

/** The entity opacity currently applied to a translucent material, or undefined when it is not translucent. */
export function getEntityOpacity(material: object | null | undefined): number | undefined {
    return material ? translucentMaterials.get(material) : undefined;
}

/** At or below this entity opacity a material is invisible, so it needs no splat depth. */
export const INVISIBLE_OPACITY_THRESHOLD = OPACITY_EPSILON;

/**
 * True when at least one of the mesh instances has an entity-translucent material that is
 * actually visible (entity opacity above ~0). Gates the splat depth pass, so an entity faded
 * fully out (e.g. an animated fade-in before its window) costs nothing.
 */
export function hasVisibleEntityTranslucency(meshInstances: ReadonlyArray<{ material: object | null }>): boolean {
    for (let i = 0; i < meshInstances.length; i++) {
        const opacity = getEntityOpacity(meshInstances[i].material);
        if (opacity !== undefined && opacity > INVISIBLE_OPACITY_THRESHOLD) return true;
    }
    return false;
}

/**
 * Register `clone` as a stand-in for `source` (a clone made with material.clone(), which copies
 * the blend/depth state, shader chunks and parameters). If the source is entity-translucent the
 * clone is registered as translucent too, with the source's original state to restore, and from
 * now on every applyEntityOpacity(source, …) is also applied to the clone.
 */
export function linkEntityOpacityClone(source: TranslucentCapableMaterial, clone: TranslucentCapableMaterial): void {
    if (source === clone) return;
    const opacity = translucentMaterials.get(source);
    if (opacity !== undefined) {
        originalState.set(clone, originalState.get(source)!);
        translucentMaterials.set(clone, opacity);
    }
    if (textureTranslucentMaterials.has(source)) textureTranslucentMaterials.add(clone);
    let clones = linkedClones.get(source);
    if (!clones) {
        clones = new Set();
        linkedClones.set(source, clones);
    }
    clones.add(clone);
}

/** Stop mirroring opacity writes from `source` onto `clone` (the clone is being discarded). */
export function unlinkEntityOpacityClone(source: TranslucentCapableMaterial, clone: TranslucentCapableMaterial): void {
    const clones = linkedClones.get(source);
    if (!clones) return;
    clones.delete(clone);
    if (clones.size === 0) linkedClones.delete(source);
}

/**
 * Opacity at which a saved hotspot sphere or portal sphere / panorama globe with a STATIC opacity
 * renders fully opaque. The viewer has treated those values as opaque since before entity
 * opacity existed, so scenes saved at e.g. 0.97 keep their look (and do not start paying for
 * the splat depth pass).
 */
export const LEGACY_SPHERE_OPAQUE_THRESHOLD = 0.95;
export function snapLegacySphereOpacity(opacity: number): number {
    return opacity >= LEGACY_SPHERE_OPAQUE_THRESHOLD ? 1 : opacity;
}

/**
 * Apply an entity-level opacity to a material.
 * - opacity >= FULL_OPACITY_THRESHOLD: the original blend mode, depth flags and shader chunks are
 *   restored (no-op for a material that was never made translucent), unless the material is
 *   marked texture-translucent (setTextureTranslucent), which stays translucent at opacity 1.
 * - otherwise: alpha blending (the material's own blend mode is kept when it already blends),
 *   depth test on, depth write off, and the splat-depth-aware opacity chunks with the opacity as
 *   a uniform, so later opacity changes are a uniform write, not a shader rebuild.
 *
 * Returns 'switched' when the material moved between opaque and translucent (the caller's cue to
 * move the entity between HotspotMeshLayer and TranslucentEntityLayer), 'updated' when only the
 * opacity uniform changed, and 'unchanged' otherwise.
 */
export type EntityOpacityChange = 'unchanged' | 'updated' | 'switched';
const CHANGE_RANK: Record<EntityOpacityChange, number> = { unchanged: 0, updated: 1, switched: 2 };
export function applyEntityOpacity(material: TranslucentCapableMaterial, opacity: number): EntityOpacityChange {
    let change = applyToMaterial(material, opacity);
    const clones = linkedClones.get(material);
    if (clones) {
        for (const clone of clones) {
            const cloneChange = applyToMaterial(clone, opacity);
            if (CHANGE_RANK[cloneChange] > CHANGE_RANK[change]) change = cloneChange;
        }
    }
    return change;
}

/** True when the material's own texture alpha keeps it translucent at full entity opacity. */
export function isTextureTranslucentMaterial(material: object | null | undefined): boolean {
    return !!material && textureTranslucentMaterials.has(material);
}

/**
 * Mark (or unmark) a material whose own texture alpha must blend over the splats, e.g. the
 * material of a transparent video plane. A marked material is rendered translucent (alpha
 * blending, no depth write, splat-depth fade, drawn after the splats in TranslucentEntityLayer)
 * at every entity opacity, including 1; unmarking returns it to the opaque path at full opacity.
 * The current entity opacity is kept. Linked clones follow. Returns the change, like
 * applyEntityOpacity ('switched' means the entity must move layers).
 */
export function setTextureTranslucent(material: TranslucentCapableMaterial, translucent: boolean): EntityOpacityChange {
    if (textureTranslucentMaterials.has(material) === translucent) return 'unchanged';
    const clones = linkedClones.get(material);
    for (const target of [material, ...(clones ?? [])]) {
        if (translucent) textureTranslucentMaterials.add(target);
        else textureTranslucentMaterials.delete(target);
    }
    return applyEntityOpacity(material, translucentMaterials.get(material) ?? 1);
}

function applyToMaterial(material: TranslucentCapableMaterial, opacity: number): EntityOpacityChange {
    const target = clamp01(Number.isFinite(opacity) ? opacity : 1);
    const wasTranslucent = translucentMaterials.has(material);

    if (target >= FULL_OPACITY_THRESHOLD && !textureTranslucentMaterials.has(material)) {
        if (!wasTranslucent) return 'unchanged';
        const original = originalState.get(material)!;
        translucentMaterials.delete(material);
        material.blendType = original.blendType;
        material.depthWrite = original.depthWrite;
        material.depthTest = original.depthTest;
        for (const [language, names] of Object.entries(original.chunks)) {
            const chunks = material.getShaderChunks(language);
            for (const [name, code] of Object.entries(names)) {
                if (code === undefined) chunks.delete(name);
                else chunks.set(name, code);
            }
        }
        material.setParameter(ENTITY_OPACITY_UNIFORM, 1);
        material.update();
        return 'switched';
    }

    if (wasTranslucent) {
        const current = translucentMaterials.get(material)!;
        if (Math.abs(current - target) < OPACITY_EPSILON) return 'unchanged';
        translucentMaterials.set(material, target);
        material.setParameter(ENTITY_OPACITY_UNIFORM, target);
        return 'updated';
    }

    const chunks: OriginalMaterialState['chunks'] = {};
    for (const [language, overrides] of Object.entries(CHUNKS)) {
        const map = material.getShaderChunks(language);
        chunks[language] = {};
        for (const [name, code] of Object.entries(overrides)) {
            chunks[language][name] = map.get(name);
            map.set(name, code);
        }
    }
    originalState.set(material, {
        blendType: material.blendType,
        depthWrite: material.depthWrite,
        depthTest: material.depthTest,
        chunks,
    });
    translucentMaterials.set(material, target);
    if (material.blendType === pc.BLEND_NONE) material.blendType = pc.BLEND_NORMAL;
    material.depthTest = true;
    material.depthWrite = false;
    material.setParameter(ENTITY_OPACITY_UNIFORM, target);
    material.update();
    return 'switched';
}

export interface OpacityAnimationLike {
    startPercent?: number;
    endPercent?: number;
    startOpacity?: number;
    endOpacity?: number;
}

export interface EntityOpacityConfigLike {
    opacityMode?: string;
    opacity?: number;
    opacityAnimation?: OpacityAnimationLike;
}

/**
 * Opacity of a tour-progress-animated entity at `scrollPercent` (0-100). Clamped to the start
 * value before the window and the end value after it, so jumping straight past the window
 * still lands on the right value. Missing fields default to 0 / 100 / visible.
 */
export function resolveAnimatedOpacity(anim: OpacityAnimationLike | undefined, scrollPercent: number): number {
    const startPercent = anim?.startPercent ?? 0;
    const endPercent = anim?.endPercent ?? 100;
    const startOpacity = clamp01(anim?.startOpacity ?? 1);
    const endOpacity = clamp01(anim?.endOpacity ?? 1);
    if (scrollPercent <= startPercent) return startOpacity;
    if (scrollPercent >= endPercent) return endOpacity;
    const progress = (scrollPercent - startPercent) / (endPercent - startPercent);
    return clamp01(startOpacity + (endOpacity - startOpacity) * progress);
}

/** Effective opacity of an entity config right now: its static value, or the animated value at `scrollPercent`. */
export function resolveEntityOpacity(config: EntityOpacityConfigLike, scrollPercent: number): number {
    if (config.opacityMode === 'animated' && config.opacityAnimation) {
        return resolveAnimatedOpacity(config.opacityAnimation, scrollPercent);
    }
    return clamp01(config.opacity ?? 1);
}
